import { useState, type FormEvent } from 'react';
import { TruckIcon } from 'lucide-react';
import { btnPrimary, inputCls } from '../ui/styles';
import { carriers, isValidTrackingCode, normalizeTrackingCode } from '../../core/rules/tracking';
import type { Carrier, Tracking } from '../../core/types/account';
type Props = {
  orderId: string;
  onShip: (tracking?: Tracking) => void;
};

/**
 * "Verzonden" met track & trace. De code is optioneel: zonder code krijgt de klant
 * een mail dat het pakket onderweg is, en ziet ze in Mijn omgeving waar ze de code
 * vindt. Met code staat de volglink direct in de mail én in Mijn omgeving.
 */
export function ShipForm({
  orderId,
  onShip
}: Props) {
  const [carrier, setCarrier] = useState<Carrier>(carriers[0]);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const codeId = `ship-code-${orderId}`;
  const errorId = `${codeId}-error`;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const clean = normalizeTrackingCode(code);
    if (clean && !isValidTrackingCode(clean)) {
      setError('Deze code klopt niet: alleen letters en cijfers, minstens 8 tekens.');
      return;
    }
    onShip(clean ? {
      carrier,
      code: clean
    } : undefined);
  };
  return <form onSubmit={submit} className="space-y-2" noValidate data-id="element-1811" data-mp="2z9ilbj6wouls">
      <p className="text-[12px] text-muted" data-id="element-1812" data-mp="347keiwy1qaaz">Track & trace (optioneel)</p>
      <div className="flex gap-2" data-id="element-1813" data-mp="349n4jgz783fp">
        <label className="sr-only" htmlFor={`${codeId}-carrier`} data-id="element-1814" data-mp="26k1xkyu8e1vw">
          Vervoerder
        </label>
        <select id={`${codeId}-carrier`} value={carrier} onChange={e => setCarrier(e.target.value as Carrier)} className={`${inputCls} w-auto`} data-id="element-1815" data-mp="2bid6n7cpc8d2">
          {carriers.map(c => <option key={c} data-id="element-1816" data-mp="2lh2s1a3t4ay2">{c}</option>)}
        </select>
        <label className="sr-only" htmlFor={codeId} data-id="element-1817" data-mp="2lidvt9x95ip6">
          Barcode
        </label>
        <input id={codeId} value={code} onChange={e => {
        // Wat je ziet is wat er wordt opgeslagen: hoofdletters in de state, niet alleen via CSS.
        // Spaties blijven tijdens het typen staan (geplakte codes) en gaan er bij versturen uit.
        setCode(e.target.value.toUpperCase());
        if (error) setError('');
      }} onBlur={() => setCode(c => normalizeTrackingCode(c))} autoCapitalize="characters" placeholder="Barcode, bijv. 3SABC123456" autoComplete="off" spellCheck={false} aria-invalid={error ? true : undefined} aria-describedby={error ? errorId : undefined} className={`${inputCls} min-w-0 flex-1 font-mono tracking-wide`} data-id="element-1818" data-mp="3rwmv01gy1x3w" />
      </div>
      {error && <p id={errorId} role="alert" className="text-[12px] text-clay" data-id="element-1819" data-mp="1mxn6huxur71n">
          {error}
        </p>}
      <button type="submit" className={`${btnPrimary} w-full`} data-id="element-1820" data-mp="ufhuicqma0pw">
        <TruckIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-1821" data-mp="udye8m4ynpo4" />
        {code.trim() ? 'Verzonden, met track & trace' : 'Verzonden, zonder code'}
      </button>
    </form>;
}